import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { verify2FA, getConfig } from '../services/api';

export default function LoginPage() {
  const { login, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);

  useEffect(() => {
    getConfig().then((cfg) => setRegistrationEnabled(cfg.publicRegistrationEnabled)).catch(() => {});
  }, []);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.requires2FA) {
        setTempToken(data.tempToken);
        setStep('2fa');
      } else if (data.requires2FASetup) {
        navigate('/account/security/setup', { state: { tempToken: data.tempToken, forced: true } });
      } else {
        navigate('/receipts');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verify2FA(tempToken, code);
      await refreshUser();
      navigate('/receipts');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: '1rem',
      boxSizing: 'border-box',
    }}>
      <div className="card" style={{ width: '100%', maxWidth: 360 }}>
        <h1 style={{ marginBottom: '0.25rem' }}>PriceTracker</h1>
        <p className="muted" style={{ marginBottom: '1.5rem', fontSize: 13 }}>
          Norwegian grocery price analytics
        </p>

        {step === 'credentials' ? (
          <form onSubmit={handleCredentialsSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="login-email" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Email</label>
              <input
                id="login-email"
                name="username"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="login-password" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Password</label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="error">{error}</p>}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%', padding: '0.65rem', marginTop: '0.5rem', fontSize: 15 }}
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <p className="muted" style={{ marginBottom: '1rem', fontSize: 13 }}>
              Enter the 6-digit code from your authenticator app, or a recovery code.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label htmlFor="login-2fa-code" style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>Code</label>
              <input
                id="login-2fa-code"
                name="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
              />
            </div>
            {error && <p className="error">{error}</p>}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
              style={{ width: '100%', padding: '0.65rem', marginTop: '0.5rem', fontSize: 15 }}
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}

        {step === 'credentials' && registrationEnabled && (
          <p className="muted" style={{ fontSize: 13, marginTop: '1rem', textAlign: 'center' }}>
            <Link to="/register">Create an account</Link>
          </p>
        )}
      </div>
    </div>
  );
}
